/**
 * Global sliding-window rate floor for the OCS client.
 *
 * Scoped per resellerId (derived from the API token).  Lives on a module-level
 * Map so it is shared across all OcsClient instances within one Worker isolate.
 *
 * Enforces ≤ 10 calls per 1000ms per reseller key.
 * Uses a sliding-window log: tracks the last 10 call timestamps.
 * Before the 11th call, waits until the oldest recorded call is at least
 * 1000ms in the past — guaranteeing ≤10 in any 1000ms span.
 *
 * Uses a serialised promise-chain (gate) so concurrent callers queue up
 * atomically. Without serialisation, multiple callers could read the same
 * window state simultaneously and all proceed, defeating the floor.
 *
 * Set env var RATE_FLOOR_DISABLED=true to bypass (never do this in production).
 */

const RATE_LIMIT = 10;   // max calls per window
const WINDOW_MS = 1000;  // window size in ms

interface WindowState {
  /** Timestamps of the last RATE_LIMIT calls (oldest first). */
  log: number[];
  /** Serialisation gate: every acquire chains onto this promise. */
  gate: Promise<void>;
}

// Module-level — shared across all OcsClient instances per isolate.
const windows = new Map<string, WindowState>();

function getWindowState(key: string): WindowState {
  let s = windows.get(key);
  if (!s) {
    s = { log: [], gate: Promise.resolve() };
    windows.set(key, s);
  }
  return s;
}

/**
 * Acquire one slot for the given reseller key, awaiting if necessary.
 * No-op when RATE_FLOOR_DISABLED=true.
 */
export async function acquireToken(resellerId: string): Promise<void> {
  // Allow env-based bypass for integration/load tests that need to spike.
  // Tests asserting the floor works must NOT set this flag.
  // Resolve `process` via globalThis so TS doesn't need @types/node
  // (Workers tsconfig excludes Node types; runtime guard handles both envs).
  const proc = (globalThis as Record<string, unknown>)["process"] as
    | { env?: Record<string, string | undefined> }
    | undefined;
  if (
    proc?.env?.["RATE_FLOOR_DISABLED"] === "true" ||
    (globalThis as Record<string, unknown>)["RATE_FLOOR_DISABLED"] === "true"
  ) {
    return;
  }

  const state = getWindowState(resellerId);

  // Chain onto the existing gate so only one acquire runs at a time.
  const ticket = state.gate.then(async () => {
    while (true) {
      const now = Date.now();

      if (state.log.length < RATE_LIMIT) {
        state.log.push(now);
        return;
      }

      const oldest = state.log[0]!;
      const age = now - oldest;

      if (age >= WINDOW_MS) {
        state.log.shift();
        state.log.push(now);
        return;
      }

      const waitMs = WINDOW_MS - age + 1;
      await new Promise<void>((r) => setTimeout(r, waitMs));
    }
  });

  // Advance the gate so the next caller queues behind this one.
  state.gate = ticket;

  return ticket;
}

/** Exposed for testing: reset all window state. */
export function _resetBucketsForTest(): void {
  windows.clear();
}
